import type {
  ExampleParagraph,
  StyleProfile,
  StyleRule,
  WritingStyle
} from '@/types/writingStyle';
import { writingStyleHelper } from '@/utils/writingStyleHelpers';

const STORAGE_KEY = 'app_writing_style';

interface WritingStyleStorage {
  [manuscriptId: string]: WritingStyle;
}

const readStorage = (): WritingStyleStorage => {
  if (typeof window === 'undefined') {
    return {};
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    return JSON.parse(raw) as WritingStyleStorage;
  } catch (error) {
    console.error('Failed to read writing style storage.', error);
    return {};
  }
};

const writeStorage = (storage: WritingStyleStorage): void => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(storage));
  } catch (error) {
    console.error('Failed to write writing style storage.', error);
  }
};

const now = (): string => new Date().toISOString();

export const writingStyleService = {
  get(manuscriptId: string): WritingStyle | null {
    const storage = readStorage();
    const value = storage[manuscriptId];
    return value ? { ...value } : null;
  },

  save(style: WritingStyle): void {
    const storage = readStorage();
    storage[style.manuscriptId] = {
      ...style,
      exampleParagraphs: style.exampleParagraphs.map((paragraph) => ({ ...paragraph, highlightedTechniques: paragraph.highlightedTechniques ? [...paragraph.highlightedTechniques] : [] })),
      styleRules: style.styleRules.map((rule) => ({ ...rule }))
    };
    writeStorage(storage);
  },

  clear(manuscriptId: string): void {
    const storage = readStorage();
    delete storage[manuscriptId];
    writeStorage(storage);
  },

  create(manuscriptId: string): WritingStyle {
    return writingStyleHelper.createEmptyWritingStyle(manuscriptId);
  },

  updateProfile(style: WritingStyle, profile: StyleProfile): WritingStyle {
    return {
      ...style,
      styleProfile: {
        ...profile
      },
      updatedAt: now()
    };
  },

  addParagraph(style: WritingStyle, paragraph: ExampleParagraph): WritingStyle {
    return {
      ...style,
      exampleParagraphs: [...style.exampleParagraphs, paragraph],
      updatedAt: now()
    };
  },

  updateParagraph(style: WritingStyle, paragraphId: string, updates: Partial<ExampleParagraph>): WritingStyle {
    const index = style.exampleParagraphs.findIndex((paragraph) => paragraph.id === paragraphId);
    if (index === -1) {
      throw new Error('Paragraph not found.');
    }
    const original = style.exampleParagraphs[index];
    const updated: ExampleParagraph = {
      ...original,
      ...updates,
      highlightedTechniques: updates.highlightedTechniques ?? original.highlightedTechniques,
      uiState: {
        ...original.uiState,
        ...(updates.uiState ?? {})
      },
      updatedAt: now()
    };
    const paragraphs = [...style.exampleParagraphs];
    paragraphs.splice(index, 1, updated);
    return {
      ...style,
      exampleParagraphs: paragraphs,
      updatedAt: now()
    };
  },

  removeParagraph(style: WritingStyle, paragraphId: string): WritingStyle {
    return {
      ...style,
      exampleParagraphs: style.exampleParagraphs.filter((paragraph) => paragraph.id !== paragraphId),
      updatedAt: now()
    };
  },

  addRule(style: WritingStyle, rule: StyleRule): WritingStyle {
    return {
      ...style,
      styleRules: [...style.styleRules, rule],
      updatedAt: now()
    };
  },

  updateRule(style: WritingStyle, ruleId: string, updates: Partial<StyleRule>): WritingStyle {
    const index = style.styleRules.findIndex((rule) => rule.id === ruleId);
    if (index === -1) {
      throw new Error('Style rule not found.');
    }
    const original = style.styleRules[index];
    const updated: StyleRule = {
      ...original,
      ...updates,
      uiState: {
        ...original.uiState,
        ...(updates.uiState ?? {})
      },
      updatedAt: now()
    };
    const rules = [...style.styleRules];
    rules.splice(index, 1, updated);
    return {
      ...style,
      styleRules: rules,
      updatedAt: now()
    };
  },

  removeRule(style: WritingStyle, ruleId: string): WritingStyle {
    return {
      ...style,
      styleRules: style.styleRules.filter((rule) => rule.id !== ruleId),
      updatedAt: now()
    };
  }
};

